import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, PauseCircle, Save } from 'lucide-react';
import { EMAIL_LIMIT_MAX, SWITCHABLE_EMAIL_KINDS, type EmailSwitchMap, type OwnerEmailSettingsDto, type OwnerPausedTenantDto } from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Field, Input } from '@/components/ui/primitives';
import { DataTable } from '@/components/ui/layout';
import { useToast } from '@/components/ui/toast';
import { emailKindLabel } from '../admin/EmailSettingsTab';
import { EmailQuotaDialog, OWNER_EMAIL_PAUSED_KEY } from './EmailQuotaDialog';
import { TotpField, isValidTotp } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';

const SETTINGS_KEY = ['owner', 'email', 'settings'] as const;

function parseLimit(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number.parseInt(value, 10);
  return parsed <= EMAIL_LIMIT_MAX ? parsed : null;
}

export function OwnerEmailPanel() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const canUpdate = useOwnerCapability('email.update');
  const [perDay, setPerDay] = useState('');
  const [perMonth, setPerMonth] = useState('');
  const [switches, setSwitches] = useState<EmailSwitchMap>({});
  const [totpCode, setTotpCode] = useState('');
  const [open, setOpen] = useState<OwnerPausedTenantDto | null>(null);

  const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => get<OwnerEmailSettingsDto>('/owner/email/settings') });
  const paused = useQuery({ queryKey: OWNER_EMAIL_PAUSED_KEY, queryFn: () => get<OwnerPausedTenantDto[]>('/owner/email/paused') });

  useEffect(() => {
    if (!settings.data) return;
    setPerDay(String(settings.data.tenantEmailsPerDay));
    setPerMonth(String(settings.data.tenantEmailsPerMonth));
    setSwitches(settings.data.switches);
  }, [settings.data]);

  const day = parseLimit(perDay);
  const month = parseLimit(perMonth);

  const save = useMutation({
    mutationFn: () => put<OwnerEmailSettingsDto>('/owner/email/settings', { tenantEmailsPerDay: day, tenantEmailsPerMonth: month, switches, totpCode }),
    onSuccess: (next) => {
      queryClient.setQueryData(SETTINGS_KEY, next);
      setTotpCode('');
      toast.success('Email limits and switches saved');
    },
    onError: (error) => toast.error('Could not save the email settings', describeError(error)),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Mail className="h-4 w-4" /> Facility email through the platform mail server
          </CardTitle>
          <CardDescription>
            Facilities without their own mail server send through the SMTP on the Settings tab. These limits apply to each of them unless the facility row sets its own. 0 means no limit. When a facility reaches a limit, all of its email stops and waits until someone resumes it.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {settings.data && !settings.data.ownerSmtpConfigured && (
            <Alert variant="warning" className="sm:col-span-2">
              No platform SMTP is saved, so facilities without their own mail server cannot send email.
            </Alert>
          )}
          <Field label="Emails per day, per facility" htmlFor="platform-email-day">
            <Input id="platform-email-day" inputMode="numeric" disabled={!canUpdate} value={perDay} onChange={(e) => setPerDay(e.target.value.replace(/\D/g, '').slice(0, 8))} />
          </Field>
          <Field label="Emails per month, per facility" htmlFor="platform-email-month">
            <Input id="platform-email-month" inputMode="numeric" disabled={!canUpdate} value={perMonth} onChange={(e) => setPerMonth(e.target.value.replace(/\D/g, '').slice(0, 8))} />
          </Field>
          <div className="space-y-2 sm:col-span-2">
            <p className="text-xs font-medium">Kinds of email allowed on the whole platform</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {SWITCHABLE_EMAIL_KINDS.map((kind) => (
                <Checkbox
                  key={kind}
                  disabled={!canUpdate}
                  checked={switches[kind] ?? true}
                  onCheckedChange={(checked) => setSwitches({ ...switches, [kind]: checked })}
                  label={emailKindLabel(kind)}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">A switched-off kind is never sent and shows as skipped in the facility email log.</p>
          </div>
          {canUpdate ? (
            <div className="space-y-3 sm:col-span-2">
              <TotpField value={totpCode} onChange={setTotpCode} />
              <Button className="gap-1.5" loading={save.isPending} disabled={day === null || month === null || !isValidTotp(totpCode)} onClick={() => save.mutate()}>
                <Save className="h-3.5 w-3.5" /> Save email limits
              </Button>
            </div>
          ) : (
            <Alert className="sm:col-span-2">Only the Owner can change the email limits and switches.</Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <PauseCircle className="h-4 w-4" /> Paused facilities
          </CardTitle>
          <CardDescription>Email stays paused until it is resumed here or by the facility Super Admin, and only once the limit allows it.</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            rows={paused.data}
            loading={paused.isLoading}
            error={paused.error}
            rowKey={(row) => row.tenantId}
            onRowClick={setOpen}
            empty="No facility is paused."
            columns={[
              { header: 'Facility', cell: (row) => <span className="font-medium">{row.name}</span> },
              { header: 'Limit', cell: (row) => (row.reason === 'monthly_limit' ? 'monthly' : 'daily') },
              { header: 'Since', cell: (row) => dateTime(row.pausedAt) },
            ]}
          />
        </CardContent>
      </Card>

      {open && <EmailQuotaDialog tenant={{ id: open.tenantId, name: open.name, slug: open.slug }} onClose={() => setOpen(null)} />}
    </div>
  );
}
